import React, { useEffect } from 'react'
import {
      CheckCircleOutlined,
      ClockCircleOutlined,
      CloseCircleOutlined,
      ExclamationCircleOutlined,
      MinusCircleOutlined,
      SyncOutlined,
} from '@ant-design/icons'
import { Select, Tag } from 'antd'
import { useTranslation } from 'react-i18next'

type AppProps = {
  value?: string
  onChange?: (value: string) => void
  options?: string[]
}

const App: React.FC<AppProps> = ({ value, onChange, options = [] }) => {
  const { t } = useTranslation()
  
  const statusMap: Record<string, any> = {
    ready: {
      color: 'green',
      icon: <CheckCircleOutlined />,
      title: t('mlops.Status.ready'),
    },
    ok: {
      color: 'green',
      icon: <CheckCircleOutlined />,
      title: t('mlops.Status.ok'),
    },
    success: {
      color: 'green',
      icon: <CheckCircleOutlined />,
      title: t('mlops.Status.success'),
    },
    succeeded: {
      color: 'green',
      icon: <CheckCircleOutlined />,
      title: t('mlops.Status.success'),
    },
    running: {
      color: 'green',
      icon: <SyncOutlined spin />,
      title: t('mlops.Status.running'),
    },
    creating: {
      color: 'blue',
      icon: <SyncOutlined spin />,
      title: t('mlops.Status.creating'),
    },
    created: {
      color: 'blue',
      icon: <SyncOutlined spin />,
      title: t('mlops.Status.creating'),
    },
    error: {
      color: 'red',
      icon: <CloseCircleOutlined />,
      title: t('mlops.Status.error'),
    },
    starting: {
      color: 'gold',
      icon: <ClockCircleOutlined />,
      title: t('mlops.Status.starting'),
    },
    stopping: {
      color: 'gold',
      icon: <ClockCircleOutlined />,
      title: t('mlops.Status.stopping'),
    },
    pending: {
      color: 'gold',
      icon: <ClockCircleOutlined />,
      title: t('mlops.Status.pending'),
    },
    queuing: {
      color: 'orange',
      icon: <ClockCircleOutlined />,
      title: t('mlops.Status.queuing'),
    },
    stopped: {
      color: 'default',
      icon: <MinusCircleOutlined />,
      title: t('mlops.Status.stopped'),
    },
    init: {
      color: 'blue',
      icon: <ExclamationCircleOutlined />,
      title: t('mlops.Status.init'),
    },
    terminated: {
      color: 'default',
      icon: <MinusCircleOutlined />,
      title: t('mlops.Status.stopped'),
    },
    failed: {
      color: 'red',
      icon: <CloseCircleOutlined />,
      title: t('mlops.Status.failed'),
    },
    failure: {
      color: 'red',
      icon: <CloseCircleOutlined />,
      title: t('mlops.Status.failed'),
    },
    warning: {
      color: 'yellow',
      icon: <CloseCircleOutlined />,
      title: t('mlops.Status.warning'),
    },
    online: {
      color: 'green',
      icon: <CheckCircleOutlined />,
      title: t('mlops.Status.online'),
    },
    offline: {
      color: 'default',
      icon: <MinusCircleOutlined />,
      title: t('mlops.Status.offline'),
    },
    open: {
      color: 'green',
      icon: <CheckCircleOutlined />,
      title: t('mlops.Status.open'),
    },
    close: {
      color: 'default',
      icon: <MinusCircleOutlined />,
      title: t('mlops.Status.close'),
    },
    unknown: {
      color: 'default',
      icon: <MinusCircleOutlined />,
      title: t('mlops.Status.unknown'),
    },
    closing: {
      color: 'gold',
      icon: <ClockCircleOutlined />,
      title: t('mlops.Status.closing'),
    },
    importing: {
      color: 'blue',
      icon: <SyncOutlined spin />,
      title: t('mlops.Status.importing'),
    },
    published: {
      color: 'green',
      icon: <CheckCircleOutlined />,
      title: t('mlops.Status.published'),
    },
    scaling: {
      color: 'gold',
      icon: <ClockCircleOutlined />,
      title: t('mlops.Status.scaling'),
    },
  }

  // 如果有 onChange 且 value 为空且 options 有内容，自动选第一个，但用 useEffect
  useEffect(() => {
    if (onChange && !value && options.length) {
      onChange(options[0])
    }
    // 只在 value, options, onChange 变化时触发
  }, [onChange, value, options])

  if (!onChange) {
    const status = value ? statusMap[value?.toLowerCase()] : undefined
    const { title, icon, color } = status || {
      color: 'default',
      icon: <MinusCircleOutlined />,
      title: value,
    }

    return (
      <Tag icon={icon} color={color}>
        {title}
      </Tag>
    )
  }

  return (
    <Select value={value} onChange={onChange}>
      {options.map(option => (
        <Select.Option key={option} value={option}>
          <Tag icon={statusMap[option?.toLowerCase()]?.icon} color={statusMap[option?.toLowerCase()]?.color}>
            {statusMap[option?.toLowerCase()]?.title || option}
          </Tag>
        </Select.Option>
      ))}
    </Select>
  )
}

export default App
